import React from "react";
import { Clock3 } from "lucide-react";
import helpers from "../../../shared/lib/helpers";
import { useItemForm } from "../model/ItemFormContext";
import ShopItemSearchModal from "./ShopItemSearchModal";
import ItemComparisonFields from "./ItemComparisonFields";
import DevOnly from "../../../shared/ui/DevOnly";
import ItemGroupActivationFields from "./ItemGroupActivationFields";
import ItemPriceAdjustmentFields from "./ItemPriceAdjustmentFields";
import { fetchUserShop, fetchUserShopOriginProductId } from "../../shops/shops.api";
import { openExternalUrl } from "../../auth/desktopProxy";
import type { ItemGroupSummary } from "../../item-groups/item-groups.api";

interface ItemBasicFieldsProps {
  auth: any;
  shops: any[];
  shop: any;
  setErrorMessage: (message: string | null) => void;
  item: any;
  itemGroups: any;
  onItemGroupCreated: (group: ItemGroupSummary) => void;
  userAdjustConfig: any;
  scheduleResolved: any;
}

const isEmptyOrZero = (value: unknown) => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string" && !value.trim()) {
    return true;
  }
  return Number(value) === 0;
};

const ItemBasicFields: React.FC<ItemBasicFieldsProps> = ({
  auth,
  shops,
  shop,
  setErrorMessage,
  item,
  itemGroups,
  onItemGroupCreated,
  userAdjustConfig,
  scheduleResolved,
}) => {
  const { register, watch, trigger, errors, setValue, getValues } = useItemForm();

  // States
  const [shopItemModalShow, setShopItemModalShow] = React.useState(false);
  const [fetchedShop, setFetchedShop] = React.useState<any>(null);

  const compareSite = watch("compare_site");
  const itemName = watch("name");
  const shopId = watch("shop_id");
  const shopItemId = watch("shop_item_id");
  const shopItemGid = watch("shop_item_gid");

  // Watch form values
  const adjustStrategy = watch("adjust_strategy");
  const adjustNoExposedUseGlobal = watch("adjust_no_exposed.use_global");
  const adjustNoExposedIsEnabled = watch("adjust_no_exposed.is_enabled");
  const rankPriceBasis = watch("rank_price_basis");
  const gapPriceBasis = watch("gap_price_basis");
  const targetRank = watch("target_rank");

  // shop_id가 변경될 때 shop 정보 가져오기
  React.useEffect(() => {
    if (!shopId) {
      setFetchedShop(null);
      return;
    }
    const loadShop = async () => {
      try {
        const shopData = await fetchUserShop(shopId, auth);
        setFetchedShop(shopData);
      } catch (error) {
        console.error("Failed to fetch shop:", error);
      }
    };
    void loadShop();
  }, [shopId, auth]);

  const isSmartstore = fetchedShop?.market_site === "smartstore" || shop?.market_site === "smartstore";
  const isSsgdotcom = fetchedShop?.market_site === "ssgdotcom" || shop?.market_site === "ssgdotcom";
  const isLotteon = fetchedShop?.market_site === "lotteon" || shop?.market_site === "lotteon";
  const isProd = import.meta.env.VITE_APP_ENV === "PROD";
  const showOriginProductIdLookup =
    (!isProd || !String(shopItemGid || "").trim()) && (!shop || isSmartstore);

  const handleLookupOriginProductId = async () => {
    if (!shop) {
      setErrorMessage("상점을 먼저 지정해 주세요.");
      return;
    }
    if (!shopItemId || shop.market_site !== "smartstore") return;

    try {
      setErrorMessage(null);
      const productInfo = await fetchUserShopOriginProductId({
        auth,
        shop_id: shop.id,
        channel_product_id: shopItemId,
      });

      setValue("shop_item_gid", productInfo.origin_product_id);
      if (!String(getValues("name") || "").trim() && productInfo.name) {
        setValue("name", productInfo.name);
      }
      if (isEmptyOrZero(getValues("price")) && productInfo.sale_price != null) {
        setValue("price", productInfo.sale_price);
      }
      if (
        isEmptyOrZero(getValues("discount")) &&
        productInfo.sale_price != null &&
        productInfo.discounted_price != null
      ) {
        setValue(
          "discount",
          productInfo.sale_price - productInfo.discounted_price,
        );
      }
    } catch (error) {
      if (!isProd) {
        console.error("Failed to lookup origin product ID:", error);
      }
    }
  };

  const isPremium = auth.subscriptionStatus?.item_is_premium;
  const maxTargetRank = 3;

  React.useEffect(() => {
    if (isPremium === false && rankPriceBasis === "price") {
      setValue("rank_price_basis", "price_exposed");
    }
    if (Number(targetRank) > maxTargetRank) {
      setValue("target_rank", 1);
    }
    // 두 맞춤 조정 방식은 프로 요금제의 판매가 차액 기준에서만 사용할 수 있다.
    if ((!isPremium || gapPriceBasis !== "price") &&
      (adjustStrategy === "adjust_price_match_discount" ||
        adjustStrategy === "adjust_discount_match_price")) {
      setValue("adjust_strategy", "adjust_price");
    }
    if ((isLotteon || isSsgdotcom) && adjustStrategy !== "adjust_price") {
      setValue("adjust_strategy", "adjust_price");
    }
  }, [adjustStrategy, gapPriceBasis, isLotteon, isSsgdotcom, isPremium, maxTargetRank, rankPriceBasis, setValue, targetRank]);

  // 설정 출처나 해당 값이 바뀌면 내 상품 지정 검증을 갱신한다.
  React.useEffect(() => {
    trigger(["my_compare_item_id", "my_direct_compare_item_id"]);
  }, [adjustNoExposedUseGlobal, adjustNoExposedIsEnabled, userAdjustConfig?.adjust_no_exposed?.is_enabled, trigger]);

  const getCompareSearchUrl = () => {
    const keyword = (itemName || "").trim();
    if (!keyword) {
      return null;
    }

    const encodedKeyword = encodeURIComponent(keyword);
    if (compareSite === "danawa") {
      return `https://search.danawa.com/dsearch.php?k1=${encodedKeyword}&module=goods&act=dispMain`;
    }
    if (compareSite === "enuri") {
      return `https://www.enuri.com/search.jsp?keyword=${encodedKeyword}`;
    }
    if (compareSite === "naver") {
      const price = Number(getValues("price"));
      const priceQuery = Number.isFinite(price) && price > 0
        ? `&maxPrice=${price}&minPrice=${price}`
        : "";
      return `https://search.shopping.naver.com/search/all?adQuery=${encodedKeyword}${priceQuery}&origQuery=${encodedKeyword}&pagingIndex=1&pagingSize=40&productSet=total&query=${encodedKeyword}&sort=rel&timestamp=&viewType=list`;
    }
    return null;
  };

  const handleOpenCompareSearch = () => {
    const searchUrl = getCompareSearchUrl();
    if (!searchUrl) {
      return;
    }
    openExternalUrl(searchUrl);
  };

  return (
    <>
      {/* ========== 상점·상품 ========== */}
      <div className="row">
        <div className="col">
          <fieldset className="border-0 p-0 m-0">
            <legend className="float-none w-auto fs-6 fw-semibold mb-3 px-0">상점·상품</legend>
            <ul className="list-unstyled">
            {/* 상점 */}
            <li className="mb-3">
              <div className="row">
                <label className="form-label">상점</label>
                <div className="col">
                  <select
                    id="id_shop_id"
                    className={`form-select ${errors.shop_id ? "is-invalid" : ""}`}
                    {...register("shop_id", { required: "상점을 선택하세요." })}
                  >
                    <option value="">-- 선택 --</option>
                    {shops?.map((shop: any) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name} (
                        {helpers.getMarketSiteName(shop.market_site)})
                      </option>
                    ))}
                  </select>
                  {errors.shop_id && (
                    <div className="invalid-feedback">
                      {errors.shop_id.message}
                    </div>
                  )}
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={!shop}
                    onClick={(_) => setShopItemModalShow(true)}
                  >
                    상품검색
                  </button>
                </div>
              </div>
            </li>

            {/* 상품ID | 상품명 */}
            <li className="mb-3 row">
              <div className="col-auto">
                <label className="form-label">
                  상품 ID
                </label>
                <input
                  type="text"
                  id="id_usershop_item_id"
                  className={`form-control ${errors.shop_item_id ? "is-invalid" : "form-disabled"}`}
                  readOnly={true}
                  {...register("shop_item_id", {
                    required: "상품 ID 입력하세요.",
                    minLength: 4,
                    maxLength: 16,
                  })}
                />
                {errors.shop_item_id && (
                  <div className="invalid-feedback">
                    {errors.shop_item_id.message}
                  </div>
                )}
                {showOriginProductIdLookup && (
                  <div className="mt-1">
                    <a
                      href="#"
                      className="btn btn-link btn-sm p-0 text-decoration-underline"
                      aria-disabled={!shopItemId}
                      onClick={(event) => {
                        event.preventDefault();
                        if (shopItemId) {
                          void handleLookupOriginProductId();
                        }
                      }}
                    >
                      원상품 ID 조회
                    </a>
                  </div>
                )}
              </div>
              <div className="col">
                <label className="form-label">
                  상품명
                  <DevOnly
                    className="ms-2"
                    visible={!isProd}
                  >
                    {getValues("shop_item_image_url")}
                  </DevOnly>
                </label>
                <div className="row">
                  <div className="col">
                    <input
                      type="text"
                      id="id_name"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      {...register("name", {
                        required: "상품명 입력하세요.",
                        minLength: 4,
                      })}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name.message}</div>
                    )}
                  </div>
                  {compareSite !== "direct" && <div className="col-auto">
                    {/*
                    입력 중인 제품명을 기준으로 가격비교 사이트에서 검색할 수 있도록 연결.
                    제품명이 없는 경우 검색 버튼 비활성화.
                    검색 버튼 클릭 시 지정된 url + 제품명으로 가격비교 사이트 검색 페이지가 새 창으로 열리도록 구현.
                    검색예시: "EB L1755U"
                    - 다나와: https://search.danawa.com/dsearch.php?k1=EB+L1755U&module=goods&act=dispMain
                    - 에누리: https://www.enuri.com/search.jsp?keyword=EB%20L1755U
                    - 네이버: 상품명과 판매가를 검색어·최소/최대 가격에 적용
                    */}
                    <button
                      className="btn btn-link btn-sm p-0 text-decoration-underline"
                      type="button"
                      disabled={!itemName || !itemName.trim()}
                      onClick={handleOpenCompareSearch}
                    >
                      {compareSite === "danawa" ? "다나와 검색" : null}
                      {compareSite === "enuri" ? "에누리 검색" : null}
                      {compareSite === "naver" ? (
                        <span style={{ lineHeight: 1.1 }}>
                          상품명으로
                          <br />
                          네이버검색
                        </span>
                      ) : null}
                    </button>
                  </div>}
                </div>
              </div>
            </li>

            {/* 기본가, 판매가, 할인액  */}
            <li className="row mb-3">
              <div className="col">
                <label className="form-label">판매가</label>
                <div className="input-group">
                  <input
                    type="text"
                    id="id_price"
                    className={`form-control ${errors.price ? "is-invalid" : "form-disabled"}`}
                    readOnly={true}
                    {...register("price")}
                  />
                  <span className="input-group-text">원</span>
                </div>
                <input type="hidden" {...register("priced_at")} />
              </div>
              <div className="col">
                <label className="form-label">할인액</label>
                <div className="input-group">
                  <input
                    type="text"
                    id="id_discount"
                    className={`form-control ${errors.discount ? "is-invalid" : "form-disabled"}`}
                    readOnly={true}
                    {...register("discount")}
                  />
                  <span className="input-group-text">원</span>
                </div>
              </div>
            </li>

            {/* 기본가, 판매가, 할인액  */}
            <li className="row mb-3">
              <div className="col">
                <label className="form-label">
                  복구기준가
                  {scheduleResolved &&
                    scheduleResolved.schedule.price_default_value !== 0 && (
                      <span className="form-text ms-2">
                        <Clock3 className="status-icon me-1" aria-hidden="true" />
                        {scheduleResolved.schedule.price_default_value > 0
                          ? `+`
                          : ``}
                        {`${scheduleResolved.schedule.price_default_value}`}
                        {scheduleResolved.schedule.price_default_value_type ===
                          "amount"
                          ? "원"
                          : "%"}
                        <span className="ms-1">
                          {scheduleResolved.schedule_type == "item"
                            ? "대체 중"
                            : "적용 중"}
                        </span>
                      </span>
                    )}
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    step="10"
                    id="id_price_default"
                    aria-describedby="price_default_help"
                    className={`form-control ${errors.price_default ? "is-invalid" : ""}`}
                    {...register("price_default", {
                      required: "복구기준가를 입력하세요.",
                      min: {
                        value: 0,
                        message: "복구기준가는 0 이상이어야 합니다.",
                      },
                    })}
                  />
                  <span className="input-group-text">원</span>
                </div>
                <div id="price_default_help" className="form-text">
                  복구시 판매가는 세팅된 기준가, 할인액은 0으로 설정됩니다.
                </div>
                {errors.price_default && (
                  <div className="invalid-feedback">
                    {errors.price_default.message}
                  </div>
                )}
              </div>
            </li>

            </ul>
          </fieldset>

          <ItemComparisonFields
            shop={shop}
            isPremium={Boolean(isPremium)}
            isSmartstore={isSmartstore}
            maxTargetRank={maxTargetRank}
            userAdjustConfig={userAdjustConfig}
            item={item}
          />
        </div>
      </div>

      <ItemPriceAdjustmentFields
        isPremium={Boolean(auth.subscriptionStatus?.item_is_premium)}
        isSmartstore={isSmartstore}
        isLotteon={isLotteon}
        isSsgdotcom={isSsgdotcom}
        userAdjustConfig={userAdjustConfig}
      />

      <ItemGroupActivationFields
        item={item}
        itemGroups={itemGroups}
        onItemGroupCreated={onItemGroupCreated}
      />

      {/* 모달들 */}
      <ShopItemSearchModal
        show={shopItemModalShow}
        onHide={async () => {
          setShopItemModalShow(false);
        }}
      />

    </>
  );
};

export default ItemBasicFields;
